import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, X, ChevronDown, ChevronUp } from 'lucide-react';

const CONSENT_KEY = 'vf_cookie_consent';
const CONSENT_PREFS_KEY = 'vf_cookie_prefs';

export const getCookieConsent = () => localStorage.getItem(CONSENT_KEY);
export const getCookiePrefs = () => {
  try { return JSON.parse(localStorage.getItem(CONSENT_PREFS_KEY) || '{}'); }
  catch { return {}; }
};
export const hasTrackingConsent = () => {
  const consent = getCookieConsent();
  if (consent === 'accepted') return true;
  if (consent === 'custom') return getCookiePrefs().fingerprinting === true;
  return false;
};
export const hasAnalyticsConsent = () => {
  const consent = getCookieConsent();
  if (consent === 'accepted') return true;
  if (consent === 'custom') return getCookiePrefs().analytics === true;
  return false;
};

const COOKIE_CATEGORIES = [
  {
    id: 'essential',
    label: 'Essential Cookies',
    description: 'Required for login, session management, and security. These cannot be disabled.',
    cookies: ['vf_session (session token)', 'vf_cookie_consent (your consent choice)', 'vf_cookie_prefs (your preference settings)'],
    locked: true,
    default: true,
  },
  {
    id: 'analytics',
    label: 'Analytics Cookies',
    description: 'Help us understand how visitors use the site so we can improve it. Data is anonymized.',
    cookies: ['PostHog (_ph_* cookies — page views, feature usage, session replays)'],
    locked: false,
    default: false,
  },
  {
    id: 'fingerprinting',
    label: 'Browser Fingerprinting',
    description: 'Creates a unique browser identifier to enforce free trial limits. This is considered tracking data under GDPR. It collects screen resolution, timezone, installed fonts, and browser plugins. No personal identity information is collected.',
    cookies: ['vf_browser_fp (locally stored fingerprint hash — FingerprintJS)'],
    locked: false,
    default: false,
  },
];

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState(() =>
    COOKIE_CATEGORIES.reduce((acc, cat) => ({ ...acc, [cat.id]: cat.default }), {})
  );

  useEffect(() => {
    if (!getCookieConsent()) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (type, preferences) => {
    localStorage.setItem(CONSENT_KEY, type);
    localStorage.setItem(CONSENT_PREFS_KEY, JSON.stringify(preferences));
    if (!preferences.fingerprinting) localStorage.removeItem('vf_browser_fp');
    setVisible(false);
    window.dispatchEvent(new Event('cookieConsentChanged'));
  };

  const handleAcceptAll = () => {
    const allOn = COOKIE_CATEGORIES.reduce((acc, cat) => ({ ...acc, [cat.id]: true }), {});
    saveConsent('accepted', allOn);
  };

  const handleRejectAll = () => {
    const essentialOnly = COOKIE_CATEGORIES.reduce(
      (acc, cat) => ({ ...acc, [cat.id]: cat.locked ? true : false }), {}
    );
    saveConsent('rejected', essentialOnly);
  };

  const handleSavePreferences = () => {
    saveConsent('custom', prefs);
  };

  const handleClose = () => {
    handleRejectAll();
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-3 md:p-6" data-testid="cookie-consent-banner">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 md:p-6">
        <div className="flex items-start gap-3">
          <div className="hidden sm:flex items-center justify-center w-9 h-9 bg-rose-100 rounded-full flex-shrink-0 mt-0.5">
            <Shield className="w-4 h-4 text-rose-500" />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-800 text-sm mb-1">We value your privacy</h3>
            <p className="text-xs text-gray-600 leading-relaxed">
              We use essential cookies to run the site. Optional cookies are used for analytics and
              free trial enforcement via browser fingerprinting (an explicit opt-in tracker).
              Closing this banner = reject all non-essential cookies.{' '}
              <Link to="/privacy" className="text-rose-500 hover:underline font-medium">Privacy Policy</Link>
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 mt-3">
              <button
                onClick={handleAcceptAll}
                className="px-4 py-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl font-medium text-xs hover:shadow-lg transition-all"
                data-testid="cookie-accept-btn"
              >
                Accept All
              </button>
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 bg-gray-800 text-white rounded-xl font-medium text-xs hover:bg-gray-900 transition-all"
                data-testid="cookie-reject-btn"
              >
                Reject All
              </button>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-medium text-xs hover:bg-gray-200 transition-all flex items-center justify-center gap-1"
                data-testid="cookie-customize-btn"
              >
                Customize
                {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            </div>

            {/* Granular Controls */}
            {showDetails && (
              <div className="mt-3 border-t border-gray-100 pt-3 space-y-2" data-testid="cookie-details-panel">
                {COOKIE_CATEGORIES.map((cat) => (
                  <div key={cat.id} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-800">{cat.label}</span>
                      {cat.locked ? (
                        <span className="text-[10px] text-gray-400 font-medium px-2 py-0.5 bg-gray-200 rounded-full">Always on</span>
                      ) : (
                        <button
                          onClick={() => setPrefs((p) => ({ ...p, [cat.id]: !p[cat.id] }))}
                          className={`relative w-9 h-5 rounded-full transition-colors ${prefs[cat.id] ? 'bg-rose-500' : 'bg-gray-300'}`}
                          data-testid={`cookie-toggle-${cat.id}`}
                          aria-label={`Toggle ${cat.label}`}
                        >
                          <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${prefs[cat.id] ? 'translate-x-4' : ''}`} />
                        </button>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">{cat.description}</p>
                    <div className="mt-1.5">
                      <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Cookies used:</p>
                      <ul className="mt-0.5">
                        {cat.cookies.map((c, i) => (
                          <li key={i} className="text-[11px] text-gray-500">• {c}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
                <button
                  onClick={handleSavePreferences}
                  className="w-full px-4 py-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl font-medium text-xs hover:shadow-lg transition-all mt-2"
                  data-testid="cookie-save-prefs-btn"
                >
                  Save Preferences
                </button>
              </div>
            )}
          </div>

          <button
            onClick={handleClose}
            className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
            aria-label="Reject all and close"
            title="Reject all non-essential cookies"
            data-testid="cookie-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
